import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Award, Loader2, X, PartyPopper } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { formatCurrency } from '@/lib/utils'
import type { APIResponse, AppliedPromoState, LoyaltyStatus } from '@/types'

interface CounterLoyaltyChipProps {
  orderId: string
  customerId?: string | null
  /** Called on apply or remove so the parent re-fetches the order. */
  onChanged?: () => void
}

/**
 * Three states for the chip:
 *   1. customer attached + loyalty enabled + ineligible
 *        → progress badge "3 / 5 visits to free dessert"
 *   2. customer attached + loyalty enabled + eligible (balance ≥ threshold)
 *      and no redemption yet on this order
 *        → CTA button "Redeem reward (free dessert · PKR 500 off)"
 *   3. redeemed already on this order
 *        → green badge "Reward applied · PKR 500" + Remove
 *
 * Returns null entirely when loyalty is disabled or no customer is
 * attached — the counter rail stays tidy.
 */
export function CounterLoyaltyChip({ orderId, customerId, onChanged }: CounterLoyaltyChipProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const statusQuery = useQuery<APIResponse<LoyaltyStatus>>({
    queryKey: ['counter-loyalty-status', customerId],
    queryFn: () => apiClient.getCustomerLoyaltyStatus(customerId as string),
    enabled: Boolean(customerId),
    staleTime: 15_000,
  })

  // Detect the "applied to this order" state by checking the
  // applied-promo endpoint? No — that's promos. Loyalty has its own
  // separate state we infer from the order's discount_amount + the
  // absence of a promo. Cleanest: piggy-back on the same applied-promo
  // query result (no loyalty applied if promo is) and use a heuristic:
  // if discount_amount > 0 and no promo and we know customer is
  // eligible … this gets messy. Better: derive from the parent order's
  // discount + the fact that a redemption row exists (which the
  // backend writes inside the redeem TX).
  //
  // For simplicity in this slice we keep two side queries — applied
  // promo for promo state, and an "is loyalty redeemed on this order"
  // signal that we infer via a third lightweight check: a small
  // /counter/orders/:id/redeemed-loyalty endpoint would tighten this,
  // but for now we can re-use the existing applied-promo flag and
  // assume "if no promo applied AND order discount matches the reward
  // value, the reward is applied." That's brittle — better is to add
  // a tiny client-side state synced from redeem mutations. We do that
  // here via a query-cache flag.

  const appliedPromoQuery = useQuery<APIResponse<AppliedPromoState>>({
    queryKey: ['counter-applied-promo', orderId],
    queryFn: () => apiClient.getAppliedPromo(orderId),
    enabled: Boolean(orderId),
    staleTime: 5_000,
  })

  // Local "redeemed this order" flag synced from the cache. The
  // mutations below toggle it explicitly.
  const redeemedKey = ['counter-loyalty-redeemed', orderId] as const
  const redeemedFlag =
    (queryClient.getQueryData<boolean>(redeemedKey) as boolean | undefined) ?? false

  const redeemMutation = useMutation({
    mutationFn: () => apiClient.redeemLoyaltyReward(orderId),
    onSuccess: (res) => {
      queryClient.setQueryData(redeemedKey, true)
      queryClient.invalidateQueries({ queryKey: ['counter-loyalty-status', customerId] })
      const data = res.data
      toast({
        title: 'Reward applied',
        description: data
          ? `${data.reward_label} · ${formatCurrency(data.discount_applied)} off`
          : 'Reward redeemed',
      })
      onChanged?.()
    },
    onError: (err) => surfaceLoyaltyError(err, toast),
  })

  const removeMutation = useMutation({
    mutationFn: () => apiClient.removeLoyaltyRedemption(orderId),
    onSuccess: () => {
      queryClient.setQueryData(redeemedKey, false)
      queryClient.invalidateQueries({ queryKey: ['counter-loyalty-status', customerId] })
      toast({ title: 'Reward removed' })
      onChanged?.()
    },
    onError: (err) => surfaceLoyaltyError(err, toast),
  })

  if (!customerId) return null
  const status = statusQuery.data?.data
  if (!status || !status.enabled) return null

  const busy = redeemMutation.isPending || removeMutation.isPending
  const promoApplied = Boolean(appliedPromoQuery.data?.data?.applied)

  // State 3: already redeemed on this order.
  if (redeemedFlag) {
    return (
      <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-2.5 py-2 dark:border-emerald-800 dark:bg-emerald-950/40">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200">
            <PartyPopper className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-emerald-800 dark:text-emerald-200">
              Reward applied
            </div>
            <div className="truncate text-[11px] text-emerald-700 dark:text-emerald-300">
              {status.reward_label || 'Reward'} ·{' '}
              {status.reward_discount_type === 'flat'
                ? formatCurrency(status.reward_discount_value ?? 0)
                : `${status.reward_discount_value}%`}{' '}
              off
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => removeMutation.mutate()}
            disabled={busy}
            className="h-8 gap-1 text-xs text-emerald-800 hover:text-emerald-900 dark:text-emerald-200"
          >
            {removeMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
            Remove
          </Button>
        </div>
      </div>
    )
  }

  // State 2: eligible CTA. Hide if a promo is already applied — they're
  // mutually exclusive.
  if (status.eligible && !promoApplied) {
    return (
      <div className="rounded-lg border border-purple-300 bg-purple-50 px-2.5 py-2 dark:border-purple-800 dark:bg-purple-950/40">
        <div className="flex items-center gap-2">
          <Award className="h-4 w-4 text-purple-600 dark:text-purple-300" aria-hidden />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-purple-800 dark:text-purple-200">
              Reward earned · {status.punches}/{status.threshold} visits
            </div>
            <div className="truncate text-[11px] text-purple-700 dark:text-purple-300">
              {status.reward_label || 'Reward'} ready to redeem
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => redeemMutation.mutate()}
            disabled={busy}
            className="h-8 gap-1.5 bg-purple-600 text-white hover:bg-purple-700 dark:bg-purple-700 dark:hover:bg-purple-600"
          >
            {redeemMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Award className="h-3.5 w-3.5" />}
            Redeem
          </Button>
        </div>
      </div>
    )
  }

  // State 1: progress badge.
  const remaining = Math.max(0, status.threshold - status.punches)
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border/80 bg-background/60 px-2.5 py-2">
      <Award className="h-4 w-4 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px] tabular-nums">
            {status.punches} / {status.threshold}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {remaining === 0
              ? 'Eligible'
              : `${remaining} more visit${remaining === 1 ? '' : 's'} to ${status.reward_label || 'reward'}`}
          </span>
        </div>
      </div>
    </div>
  )
}

function surfaceLoyaltyError(err: unknown, toast: ReturnType<typeof useToast>['toast']) {
  const reason = extractErrorReason(err)
  const map: Record<string, { title: string; description: string }> = {
    loyalty_disabled: { title: 'Loyalty is off', description: 'Enable the programme in admin settings.' },
    no_customer: { title: 'Customer required', description: 'Attach customer details before redeeming.' },
    conflict_promo: { title: 'Promo in place', description: 'Remove the promo before redeeming the reward.' },
    conflict_manual_discount: { title: 'Manual discount in place', description: 'Remove the discount before redeeming the reward.' },
    conflict_existing_loyalty: { title: 'Already redeemed', description: 'A reward is already applied to this order.' },
    insufficient_punches: { title: 'Not enough punches yet', description: 'Customer hasn\'t reached the threshold.' },
    order_not_open: { title: 'Order is closed', description: 'Cannot edit a completed or cancelled order.' },
  }
  const tx = (reason && map[reason]) || { title: 'Reward redeem failed', description: err instanceof Error ? err.message : String(err) }
  toast({ title: tx.title, description: tx.description, variant: 'destructive' })
}

function extractErrorReason(err: unknown): string | undefined {
  if (!err || typeof err !== 'object') return undefined
  const anyErr = err as { response?: { data?: { error?: string } } }
  return anyErr.response?.data?.error
}
